/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * PublicViewPreview — live iframe preview with desktop/mobile toggle.
 */

import React, { useState, useRef, useEffect } from 'react';

export default function PublicViewPreview({ previewUrl, isEverConfigured, theme }) {
  const [viewport, setViewport] = useState('desktop'); // 'desktop' | 'mobile'
  const iframeRef = useRef(null);

  const iframeWidth = viewport === 'mobile' ? 375 : '100%';

  // Post theme to the iframe so the client app applies the same CSS variables.
  const sendTheme = () => {
    if (iframeRef.current?.contentWindow && theme) {
      iframeRef.current.contentWindow.postMessage({ type: 'ARCEE_THEME', theme }, '*');
    }
  };

  // Re-send whenever the admin user switches themes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(sendTheme, [theme]);

  return (
    <div className="d-flex flex-column h-100">
      {/* Toolbar — only shown once configured */}
      {isEverConfigured && (
        <div
          className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
          style={{ background: '#f8fafc', minHeight: 48 }}
        >
          <div className="small text-muted text-truncate" style={{ maxWidth: 280 }}>
            {previewUrl
              ? <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-muted text-decoration-none">{previewUrl}</a>
              : <span className="fst-italic">Loading preview…</span>
            }
          </div>
          <div className="d-flex gap-2 align-items-center">
            {/* Viewport toggle */}
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                className={`btn ${viewport === 'desktop' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setViewport('desktop')}
                title="Desktop view"
              >
                <i className="bi bi-display" />
              </button>
              <button
                type="button"
                className={`btn ${viewport === 'mobile' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setViewport('mobile')}
                title="Mobile view"
              >
                <i className="bi bi-phone" />
              </button>
            </div>
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-outline-secondary"
                title="Open in new tab"
              >
                <i className="bi bi-box-arrow-up-right" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Preview area */}
      <div
        className="flex-grow-1 d-flex align-items-start justify-content-center overflow-auto"
        style={{ background: '#e2e8f0', padding: isEverConfigured && viewport === 'mobile' ? '16px' : 0 }}
      >
        {!isEverConfigured ? (
          /* Never been saved — show placeholder */
          <div
            className="d-flex flex-column align-items-center justify-content-center text-muted h-100 w-100"
            style={{ minHeight: 300 }}
          >
            <i className="bi bi-eye-slash mb-3" style={{ fontSize: 48, opacity: 0.3 }} />
            <div className="fw-semibold">Configure and save to preview</div>
            <div className="small mt-1 text-center px-4">The live preview will appear here.</div>
          </div>
        ) : previewUrl ? (
          /* Configured and URL ready — show iframe */
          <iframe
            ref={iframeRef}
            src={previewUrl}
            title="Public page preview"
            onLoad={sendTheme}
            style={{
              width: iframeWidth,
              height: viewport === 'mobile' ? 667 : '100%',
              border: viewport === 'mobile' ? '8px solid #1e293b' : 'none',
              borderRadius: viewport === 'mobile' ? 16 : 0,
              background: '#fff',
              minHeight: 400,
            }}
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        ) : (
          /* Configured but URL not built yet — loading */
          <div
            className="d-flex flex-column align-items-center justify-content-center text-muted h-100 w-100"
            style={{ minHeight: 300 }}
          >
            <span className="spinner-border mb-3" style={{ color: theme?.primary, opacity: 0.5 }} />
            <div className="small">Loading preview…</div>
          </div>
        )}
      </div>
    </div>
  );
}
